import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { PRIMARY_NAV, SECONDARY_NAV } from '@/data/navigation';
import { CONTACT } from '@/lib/constants';
import { LOGOS } from '@/lib/assets';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/cn';
import { buttonClasses } from '@/components/ui/buttonStyles';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  /** Fired once the overlay has finished its exit (ink retract) animation. */
  onExitComplete?: () => void;
}

/**
 * Full-screen mobile navigation overlay. Shown when the hamburger is tapped.
 * Closes on ESC, route change (via NavLink click), and X button.
 */
export function MobileMenu({ open, onClose, onExitComplete }: MobileMenuProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const handleNavClick = useCallback(
    (e: React.MouseEvent, path: string) => {
      const hashIndex = path.indexOf('#');

      if (hashIndex !== -1) {
        const route = path.slice(0, hashIndex) || '/';
        const hash = path.slice(hashIndex + 1);

        e.preventDefault();
        onClose();

        if (pathname === route) {
          setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
          }, 200);
        } else {
          void navigate(route);
          setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        }
        return;
      }

      if (pathname === path) {
        e.preventDefault();
        onClose();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        onClose();
      }
    },
    [pathname, navigate, onClose]
  );

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {open && (
        <motion.div
          // "Wet ink" reveal — purple bleeds out from the top-right corner
          // (where the hamburger lives) until it floods the whole screen.
          initial={{ clipPath: 'circle(0% at 100% 0%)' }}
          animate={{ clipPath: 'circle(150% at 100% 0%)' }}
          exit={{ clipPath: 'circle(0% at 100% 0%)' }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ willChange: 'clip-path' }}
          className="fixed inset-0 z-50 flex flex-col bg-brand-purple text-white lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navegação"
        >
          {/* Options fade in only once the ink has flooded the screen. */}
          <motion.div
            className="flex min-h-0 flex-1 flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ delay: 0.85, duration: 0.3 }}
          >
          {/* Logo — top-left, aligned to the header's container padding. */}
          <div className="pointer-events-none absolute inset-x-0 top-0">
            <div className="container-page flex h-20 items-center">
              <Link
                to={ROUTES.home}
                onClick={onClose}
                aria-label="ARCOLL TECH — Ir para a Home"
                className="pointer-events-auto"
              >
                <img src={LOGOS.icon} alt="" className="h-20 w-auto" />
              </Link>
            </div>
          </div>

          <nav
            aria-label="Navegação principal — mobile"
            className="flex-1 overflow-y-auto px-5 pt-28 pb-8"
          >
            <motion.ul
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: { delayChildren: 0.9, staggerChildren: 0.06 },
                },
              }}
              className="flex flex-col gap-1 text-2xl font-medium"
            >
              {PRIMARY_NAV.map((item) => {
                const itemVariants = {
                  hidden: { opacity: 0, x: -12 },
                  visible: { opacity: 1, x: 0 },
                };

                if (item.children) {
                  const expanded = openGroup === item.label;
                  return (
                    <motion.li key={item.label} variants={itemVariants}>
                      <button
                        type="button"
                        aria-expanded={expanded}
                        onClick={() =>
                          setOpenGroup(expanded ? null : item.label)
                        }
                        className="flex w-full items-center justify-between py-3 text-white transition-colors hover:text-brand-yellow"
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'h-6 w-6 transition-transform duration-200',
                            expanded && 'rotate-180'
                          )}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {expanded && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="overflow-hidden pl-4 text-xl"
                          >
                            {item.children.map((child) => {
                              const childPath = child.path ?? '#';
                              const childIsHash = childPath.includes('#');
                              return (
                                <li key={childPath}>
                                  <NavLink
                                    to={childPath}
                                    onClick={(e) => handleNavClick(e, childPath)}
                                    className={({ isActive }) =>
                                      cn(
                                        'block py-2.5 transition-colors',
                                        isActive && !childIsHash
                                          ? 'text-brand-yellow'
                                          : 'text-white/85 hover:text-brand-yellow'
                                      )
                                    }
                                  >
                                    {child.label}
                                  </NavLink>
                                </li>
                              );
                            })}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </motion.li>
                  );
                }

                const path = item.path ?? ROUTES.home;
                const isHash = path.includes('#');
                return (
                  <motion.li key={path} variants={itemVariants}>
                    <NavLink
                      to={path}
                      end={path === ROUTES.home}
                      onClick={(e) => handleNavClick(e, path)}
                      className={({ isActive }) =>
                        cn(
                          'block py-3 transition-colors',
                          isActive && !isHash
                            ? 'text-brand-yellow'
                            : 'text-white hover:text-brand-yellow'
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  </motion.li>
                );
              })}
            </motion.ul>

            <div className="mt-10 border-t border-white/10 pt-6">
              <p className="text-xs uppercase tracking-widest text-white/50 mb-3">
                Também pode interessar
              </p>
              <ul className="flex flex-col gap-2 text-base">
                {SECONDARY_NAV.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={onClose}
                      className="block py-2 text-white/80 hover:text-brand-yellow transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="px-5 py-5 border-t border-white/10">
            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClasses('primary-yellow', 'lg', 'w-full')}
            >
              Atendimento pelo Whatsapp
            </a>
          </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
